// Preview-Tab im Kaufland-Lister-Detail-Pane (Stufe 4 — KL-008).
//
// Konsumiert die Stufe-3-Edge-Function kaufland-listing-preview: wendet das gespeicherte
// mapping-profil + die field-mappings auf den konkreten vario-artikel + listing an und zeigt:
//   * Echte Kaufland-Push-Payloads (productData + unit)
//   * Errors + Warnings (Pflicht-checks, EAN-format, GPSR, length-limits, FR-eco)
//   * fieldPreview pro feld (resolved wert + source-label) als tabelle
//
// Vorbedingung: ein gespeichertes mapping-profil fuer (storefront, locale, category_id)
// muss existieren — wird via kaufland_get_mapping_profile geladen.

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { localeForStorefront as localeFor } from '../../lib/storefront-locale.js';

export interface PreviewTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}

export interface PreviewTabListing {
  storefront: string;
  kaufland_category_id: number | null;
  vario_listing_id: string;
  vario_article_id: string;
}

interface PreviewIssue { field: string; code: string; message: string; }
interface FieldPreviewItem {
  key: string;
  group: string;
  sourceType: 'vario_path' | 'fixed_value' | 'template' | 'unmapped';
  sourceLabel: string;
  resolvedValue: unknown;
}
interface PreviewResp {
  productDataPayload: { ean: string[]; attributes: Record<string, unknown[]> };
  unitPayload: Record<string, unknown>;
  fieldPreview: FieldPreviewItem[];
  errors: PreviewIssue[];
  warnings: PreviewIssue[];
  meta: {
    profileId: string;
    profile: { storefront: string; locale: string; categoryId: number; name: string | null; isDefault: boolean };
    articleId: string;
    listingId: string | null;
    version: string;
  };
}

interface ProfileLite { id: string; storefront: string; locale: string; category_id: number; }

const STYLES = `
  .kl-prev-shell { display: flex; flex-direction: column; gap: 0.8em; padding: 1em; }
  .kl-prev-header {
    /* Kein position:sticky — der Header (inkl. "Aktualisieren") soll im normalen
       Fluss bleiben und mit dem Inhalt wegscrollen, nicht am Rand kleben. */
    background: var(--v-color-surface, #fff);
    padding: 0.6em 0.8em;
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: var(--v-border-radius-card, 12px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .kl-prev-title { font-size: 1em; font-weight: 600; }
  .kl-prev-subtitle { font-size: 0.82em; color: var(--v-color-text-muted, #666); margin-top: 2px; }
  .kl-prev-counts { display: flex; gap: 0.4em; flex-wrap: wrap; align-items: center; }
  .kl-prev-counts .v-pill { font-size: 0.78em; }

  .kl-prev-issues { display: flex; flex-direction: column; gap: 0.4em; }
  .kl-prev-issue {
    padding: 0.5em 0.8em;
    border-radius: var(--v-border-radius, 4px);
    font-size: 0.85em;
    border-left: 3px solid;
    background: #fff;
    display: flex; gap: 0.5em; align-items: baseline;
  }
  .kl-prev-issue-error   { border-left-color: var(--v-negative, #cd4837); background: rgb(205 72 55 / 6%); }
  .kl-prev-issue-warning { border-left-color: var(--v-warning, #f0b618); background: rgb(240 182 24 / 8%); }
  .kl-prev-issue-field { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.85em; color: var(--v-color-text-muted, #666); flex-shrink: 0; }
  .kl-prev-issue-code  { font-size: 0.72em; color: var(--v-color-text-muted, #999); text-transform: uppercase; letter-spacing: 0.04em; padding: 1px 5px; border: 1px solid currentColor; border-radius: 8px; flex-shrink: 0; }

  .kl-prev-card {
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: var(--v-border-radius-card, 12px);
    background: var(--v-color-surface, #fff);
    overflow: hidden;
  }
  .kl-prev-card-head {
    padding: 0.6em 0.9em;
    background: var(--v-color-surface-muted, #f7f7f7);
    border-bottom: 1px solid var(--v-color-border, #e0e0e0);
    font-weight: 600;
    display: flex; align-items: center; gap: 0.5em;
    font-size: 0.92em;
  }
  .kl-prev-card-body { padding: 0; }

  .kl-prev-payload-pre {
    margin: 0;
    padding: 0.8em 1em;
    background: var(--v-color-surface-muted, #fafafa);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.78em;
    line-height: 1.4;
    overflow: auto;
    white-space: pre;
    max-height: 320px;
  }

  .kl-prev-table { width: 100%; border-collapse: collapse; font-size: 0.85em; }
  .kl-prev-table th, .kl-prev-table td { padding: 0.45em 0.8em; text-align: left; border-bottom: 1px solid var(--v-color-border-soft, #eee); vertical-align: top; }
  .kl-prev-table th { background: var(--v-color-surface-muted, #fafafa); font-weight: 500; color: var(--v-color-text-muted, #666); font-size: 0.78em; text-transform: uppercase; letter-spacing: 0.04em; }
  .kl-prev-table tbody tr:hover { background: var(--v-color-surface-muted, #fafafa); }
  .kl-prev-table .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.92em; }
  .kl-prev-table .resolved-empty { color: var(--v-color-text-muted, #999); font-style: italic; }
  .kl-prev-table .group-badge { font-size: 0.72em; color: var(--v-color-text-muted, #666); padding: 1px 5px; border: 1px solid var(--v-color-border, #e0e0e0); border-radius: 8px; text-transform: lowercase; }

  .kl-prev-empty {
    padding: 2em 1em;
    text-align: center;
    color: var(--v-color-text-muted, #666);
  }
  .kl-prev-empty-icon { font-size: 2.5em; color: var(--v-color-text-muted, #ccc); margin-bottom: 0.5em; }
`;

async function callFn<T>(
  ctx: PreviewTabCtx,
  name: string,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function renderPreviewTab(
  host: HTMLElement,
  ctx: PreviewTabCtx,
  listing: PreviewTabListing,
): Promise<void> {
  host.innerHTML = `
    <style>${STYLES}</style>
    <div class="kl-prev-empty">
      <div class="kl-prev-empty-icon"><span class="v-icon fa-light fa-spinner-third fa-spin"></span></div>
      <div>Lade Mapping-Profil …</div>
    </div>
  `;

  const categoryId = listing.kaufland_category_id;
  if (!categoryId) {
    renderEmpty(host, 'circle-info', 'Vorschau noch nicht möglich.', 'Dieses Listing hat noch keine Kaufland-Kategorie. Zuerst im Vario-Slot zuweisen.');
    return;
  }
  const storefront = listing.storefront;
  const locale = localeFor(storefront);

  // 1) Mapping-Profil holen, um profileId zu bekommen.
  const { data: profileResp, error: profileErr } = await ctx.supabase.rpc('kaufland_get_mapping_profile', {
    _storefront: storefront, _locale: locale, _category_id: categoryId,
  });
  if (profileErr) {
    renderError(host, `Profil-Ladung fehlgeschlagen: ${profileErr.message}`);
    return;
  }
  const profile = (profileResp as { profile?: ProfileLite | null } | null)?.profile ?? null;
  if (!profile) {
    renderEmpty(
      host,
      'sliders',
      'Noch kein Mapping-Profil gespeichert.',
      'Wechsle in den „Mapping"-Tab, definiere die Field-Mappings und klicke „Speichern". Danach kommst du hier zur Vorschau.',
    );
    return;
  }

  // 2) Preview-Edge-Function rufen.
  const previewResp = await callFn<PreviewResp>(ctx, 'kaufland-listing-preview', {
    profileId: profile.id,
    articleId: listing.vario_article_id,
    listingId: listing.vario_listing_id,
  });
  if (!previewResp.ok) {
    renderError(host, `Vorschau fehlgeschlagen: ${previewResp.error}`);
    return;
  }

  renderResult(host, ctx, listing, previewResp.value);
}

function renderResult(host: HTMLElement, ctx: PreviewTabCtx, listing: PreviewTabListing, p: PreviewResp): void {
  const errorCount = p.errors.length;
  const warningCount = p.warnings.length;
  const fieldCount = p.fieldPreview.length;
  const productKeyCount = Object.keys(p.productDataPayload.attributes).length;
  const eanCount = p.productDataPayload.ean.length;

  host.innerHTML = `
    <style>${STYLES}</style>
    <div class="kl-prev-shell">

      <div class="kl-prev-header">
        <div class="v-row v-row-sm" style="align-items:center;gap:0.5em;">
          <div class="v-col-grow" style="min-width:0;">
            <div class="kl-prev-title">Vorschau Listing-Push</div>
            <div class="kl-prev-subtitle">
              Profil <code>${escapeHtml(p.meta.profile.name ?? 'unbenannt')}</code>
              · Storefront <strong>${escapeHtml(p.meta.profile.storefront.toUpperCase())}</strong>
              · Kategorie <strong>${p.meta.profile.categoryId}</strong>
              · ${fieldCount} Mapping-Felder resolved
            </div>
          </div>
          <div class="v-col-shrink kl-prev-counts">
            ${errorCount > 0
              ? `<span class="v-pill v-pill--error"><span class="v-icon fa-light fa-circle-xmark"></span> ${errorCount} Fehler</span>`
              : `<span class="v-pill v-pill--ok"><span class="v-icon fa-light fa-check"></span> keine Fehler</span>`}
            ${warningCount > 0
              ? `<span class="v-pill v-pill--warn"><span class="v-icon fa-light fa-triangle-exclamation"></span> ${warningCount} Warnungen</span>`
              : ''}
          </div>
          <div class="v-col-shrink">
            <button type="button" class="v-button" data-action="refresh" title="Profil + Beispielartikel neu auswerten">
              <span class="v-icon fa-light fa-arrows-rotate"></span>Aktualisieren
            </button>
          </div>
        </div>
      </div>

      ${errorCount + warningCount > 0 ? `
        <div class="kl-prev-issues">
          ${p.errors.map((i) => renderIssue('error', i)).join('')}
          ${p.warnings.map((i) => renderIssue('warning', i)).join('')}
        </div>
      ` : ''}

      <div class="kl-prev-card">
        <div class="kl-prev-card-head">
          <span class="v-icon fa-light fa-cubes" style="color:var(--v-info,#11518c);"></span>
          PUT /v2/product-data - Stammdaten-Push
          <span style="margin-left:auto;font-size:0.78em;color:var(--v-color-text-muted,#666);font-weight:400;">
            ${eanCount} EAN(s) · ${productKeyCount} Attribute
          </span>
        </div>
        <div class="kl-prev-card-body">
          <pre class="kl-prev-payload-pre">${escapeHtml(JSON.stringify(p.productDataPayload, null, 2))}</pre>
        </div>
      </div>

      <div class="kl-prev-card">
        <div class="kl-prev-card-head">
          <span class="v-icon fa-light fa-tag" style="color:var(--v-secondary,#84b933);"></span>
          POST /v2/units - Angebot-Push
          <span style="margin-left:auto;font-size:0.78em;color:var(--v-color-text-muted,#666);font-weight:400;">
            ${Object.keys(p.unitPayload).length} Felder
          </span>
        </div>
        <div class="kl-prev-card-body">
          <pre class="kl-prev-payload-pre">${escapeHtml(JSON.stringify(p.unitPayload, null, 2))}</pre>
        </div>
      </div>

      <div class="kl-prev-card">
        <div class="kl-prev-card-head">
          <span class="v-icon fa-light fa-table-list"></span>
          Field-Preview - pro Feld der resolved Wert
        </div>
        <div class="kl-prev-card-body" style="max-height:400px;overflow:auto;">
          ${renderFieldTable(p.fieldPreview)}
        </div>
      </div>

      <!-- Die Vorschau der Variant-Suggestion-Zeilen ist am 2026-08-16 entfallen. Sie war
           ein Stub: Die Function lieferte hier IMMER ein leeres Array, der Block wurde also
           nie gezeigt. Zudem trennte er mit Semikolon, genau dem Trennzeichen, das den
           abgebrochenen Import verursacht hat. Die echte Datei baut variant-csv.ts im
           Worker, kommagetrennt. -->

    </div>
  `;

  host.querySelector<HTMLButtonElement>('[data-action="refresh"]')!.addEventListener('click', () => void renderPreviewTab(host, ctx, listing));
}

function renderIssue(level: 'error' | 'warning', i: PreviewIssue): string {
  return `
    <div class="kl-prev-issue kl-prev-issue-${level}">
      <span class="kl-prev-issue-code">${escapeHtml(i.code)}</span>
      <span class="kl-prev-issue-field">${escapeHtml(i.field)}</span>
      <span style="flex:1;">${escapeHtml(i.message)}</span>
    </div>
  `;
}

function renderFieldTable(fields: FieldPreviewItem[]): string {
  if (fields.length === 0) {
    return `<div class="kl-prev-empty"><span class="v-icon fa-light fa-table-list kl-prev-empty-icon"></span><div>Keine Field-Mappings - Profil ist leer.</div></div>`;
  }
  // Gruppieren nach group
  const buckets = new Map<string, FieldPreviewItem[]>();
  for (const f of fields) {
    const b = buckets.get(f.group) ?? [];
    b.push(f);
    buckets.set(f.group, b);
  }
  return `
    <table class="kl-prev-table">
      <thead>
        <tr>
          <th style="width:25%;">Feld</th>
          <th style="width:15%;">Group</th>
          <th style="width:25%;">Quelle</th>
          <th>Resolved Wert</th>
        </tr>
      </thead>
      <tbody>
        ${fields.map((f) => `
          <tr>
            <td class="mono">${escapeHtml(f.key)}</td>
            <td><span class="group-badge">${escapeHtml(f.group)}</span></td>
            <td><code>${escapeHtml(f.sourceLabel)}</code> <span style="color:var(--v-color-text-muted,#999);font-size:0.78em;">(${escapeHtml(f.sourceType)})</span></td>
            <td>${renderResolvedValue(f.resolvedValue)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

function renderResolvedValue(v: unknown): string {
  if (v === null || v === undefined || v === '') {
    return `<span class="resolved-empty">—</span>`;
  }
  if (typeof v === 'string' && v.length > 120) {
    return `<span class="mono">${escapeHtml(v.slice(0, 120))}…</span> <span style="color:var(--v-color-text-muted,#999);font-size:0.78em;">(${v.length} Zeichen)</span>`;
  }
  if (typeof v === 'object') {
    return `<code style="font-size:0.78em;">${escapeHtml(JSON.stringify(v).slice(0, 200))}</code>`;
  }
  return `<span class="mono">${escapeHtml(String(v))}</span>`;
}

function renderEmpty(host: HTMLElement, icon: string, title: string, body: string): void {
  host.innerHTML = `
    <style>${STYLES}</style>
    <div class="kl-prev-empty">
      <div class="kl-prev-empty-icon"><span class="v-icon fa-light fa-${icon}"></span></div>
      <div style="font-size:1.05em;font-weight:500;margin-bottom:0.3em;">${escapeHtml(title)}</div>
      <div style="max-width:520px;margin:0 auto;font-size:0.92em;">${escapeHtml(body)}</div>
    </div>
  `;
}

function renderError(host: HTMLElement, msg: string): void {
  host.innerHTML = `
    <style>${STYLES}</style>
    <div class="v-pa-md">
      <div class="v-alert v-alert--error">${escapeHtml(msg)}</div>
    </div>
  `;
  showToast(msg, 'error');
}

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
